<template>
  <div class="gap-2 flex">
    <p
      v-if="isOpenNow !== null"
      :class="{
        'text-green-700': isOpenNow,
        'text-red': !isOpenNow
      }"
    >
      {{ isOpenNow ? 'Open now' : 'Closed' }}
    </p>
    <p v-else>
      {{ irregularHoursText }}
    </p>
    <UIcon name="mdi:checkbox-blank-circle" class="size-0.5 mt-2.5 text-gray-700 hidden xl:block" />
    <div v-if="openingTimesType === 'standard'" class="inline-block">
      <UButton
        color="white"
        variant="ghost"
        size="xl"
        :icon="isShowingOpeningHours ? 'mdi:triangle' : 'mdi:triangle-down'"
        trailing
        :padded="false"
        :ui="{ icon: { size: { xl: 'size-2' } } }"
        class="font-poppins font-normal text-almostBlack"
        @click="isShowingOpeningHours = !isShowingOpeningHours"
      >
        <span v-if="openingTimesForCurrentDay.open24Hours">Open 24 hours</span>
        <span v-else-if="closesAt">Closes {{ closesAt }}</span>
        <span v-else-if="opensAt">Opens {{ opensAt }}</span>
        <span v-else>Check opening hours</span>
      </UButton>
      <Transition>
        <FrontendServiceOpeningHours v-if="isShowingOpeningHours" :opening-times="openingTimes" />
      </Transition>
    </div>
  </div>
</template>
<script setup lang="ts">
// Import tooling
import { formatMilitaryTime } from '~/utils/formatMilitaryTime'
import type { OpeningTimes, OpeningHours } from '~/types/OpeningTimes'
import { daysOfWeek } from '~/types/DaysOfWeek'
import { serviceIsOpenNow } from '~/utils/openingTimes/serviceIsOpenNow'
import { serviceClosesAt } from '~/utils/openingTimes/serviceClosesAt'

import type { Service } from '~/types/Service'
import { getCurrentDayOfWeek } from '~/utils/openingTimes/getCurrentDayOfWeek'
const dayjs = useDayjs()

// Get props
const props = defineProps({
  openingTimes: {
    type: Object as PropType<OpeningTimes>,
    required: true
  },
  openingTimesType: {
    type: String as PropType<Service['openingTimesType']>,
    required: true,
    default: 'standard'
  },
  completelyIrregularHoursText: {
    type: String,
    required: false,
    default: ''
  }
})

const isShowingOpeningHours = ref(false)
const { currentDayOfWeek, currentDayOfWeekIndex } = getCurrentDayOfWeek()

// Get the opening times from that day
const openingTimesForCurrentDay = props.openingTimes[currentDayOfWeek]

const openingHoursForCurrentDay: OpeningHours[] = openingTimesForCurrentDay.openingHours

// Check if service is open now
const currentTime = dayjs().format('HH:mm')

// Will set to true if current time falls between open/closing times in any of the objects inside openingHoursForCurrentDay
// Will set to null if service has irregular opening hours today
const isOpenNow = ref<boolean | null>(false)

const closesAt = ref('')
const opensAt = ref('')
const irregularHoursText = ref('')

const setCurrentOpenStatus = () => {
  // handle completely irregular hours
  if (props.openingTimesType === 'completelyIrregular') {
    isOpenNow.value = null
    irregularHoursText.value = props.completelyIrregularHoursText
    return
  }

  isOpenNow.value = serviceIsOpenNow(props.openingTimes)
  const closesTime = serviceClosesAt(props.openingTimes)
  if (closesTime) closesAt.value = formatMilitaryTime(closesTime)

  if (openingTimesForCurrentDay.hasIrregularHours === true) {
    // If there are irregular hours for today then service can't be considered open or closed so we show irregular hours text instead
    isOpenNow.value = null
    irregularHoursText.value = openingTimesForCurrentDay.irregularHoursText?.length
      ? openingTimesForCurrentDay.irregularHoursText
      : 'Irregular opening hours'
  }

  if (!isOpenNow.value) getNextOpeningTime()
}

// Find the next opening time
const getNextOpeningTime = () => {
  const nextOpeningTime = openingHoursForCurrentDay.find((openingHour) => {
    return openingHour.startTime > currentTime
  })

  // Additionally we should check that the isClosed box for today is not checked as this overrides any opening hours
  if (nextOpeningTime) {
    // Also we need to check if the isOpen24hours box is checked for the next opening time, if it is then we'll consider the opening hour to be 12am
    if (openingTimesForCurrentDay.open24Hours === true) {
      nextOpeningTime.startTime = '12am'
    } else {
      // Otherwise we'll just use the next opening time we find
      opensAt.value = formatMilitaryTime(nextOpeningTime.startTime)
    }
  } else {
    // if no opening time found for today, loop though the rest of the days until we find the next opening time
    // boot out if we end up on today again
    let amountOfLoops = 0
    let currentDayI = currentDayOfWeekIndex + 1
    while (currentDayI !== currentDayOfWeekIndex) {
      if (amountOfLoops > 7) break
      amountOfLoops++
      if (currentDayI === 7) currentDayI = 0

      // Get opening times for next day
      const nextDay = daysOfWeek[currentDayI]
      const nextOpeningTimes = props.openingTimes[nextDay]

      // If isClosed box is checked for that day then skip to the next day
      if (!nextDay || nextOpeningTimes.isClosed === true) {
        currentDayI++
        continue
      }
      // Get opening hours for that day
      const nextOpeningHours = nextOpeningTimes.openingHours
      const nextOpeningTime = nextOpeningHours[0]
      if (nextOpeningTime) {
        const nextDayDisplayText = nextDay.charAt(0).toUpperCase() + nextDay.slice(1, 3)

        // Check that the isOpen24hours box is not checked for the next opening time, if it is then we'll consider the opening hour to be 12am
        const nextOpenTime = nextOpeningTimes.open24Hours
          ? '12am'
          : formatMilitaryTime(nextOpeningTime.startTime)

        opensAt.value = `${nextDayDisplayText} ${nextOpenTime}`
        break
      } else {
        currentDayI++
      }
    }
  }
}

// ssr:
setCurrentOpenStatus()

// on mount:
onMounted(() => {
  setCurrentOpenStatus()
})
</script>
<style scoped>
.v-enter-from,
.v-leave-to {
  @apply opacity-0 transition-opacity duration-200;
}
.v-enter-to,
.v-leave-from {
  @apply opacity-100 transition-opacity duration-200;
}
</style>
